import { fileIconForName } from "@caliu/utils";
import { useEffect, useState } from "react";
import type { ProjectedAttachment } from "../../lib/attachmentProjection";
import { FILE_ICONS } from "../../lib/fileIcons";
import {
  getLocalAttachment,
  subscribeLocalAttachment,
} from "../../lib/localAttachmentCache";
import { Icon } from "@/components/ui/Icon";

/** Square image thumbnail. Resolves the canonical URL through the local
 *  blob cache so a just-added (not-yet-uploaded) image shows instantly; a
 *  persisted image loads from the server via the same-site cookie-authed GET. */
function AttachmentThumb({ src, alt }: { src: string; alt: string }) {
  const [displaySrc, setDisplaySrc] = useState(() => getLocalAttachment(src) ?? src);

  useEffect(() => {
    setDisplaySrc(getLocalAttachment(src) ?? src);
    return subscribeLocalAttachment((url) => {
      if (url === src) setDisplaySrc(getLocalAttachment(src) ?? src);
    });
  }, [src]);

  return (
    <img
      src={displaySrc}
      alt={alt}
      loading="lazy"
      draggable={false}
      className="h-11 w-11 shrink-0 rounded-md object-cover bg-border-light"
    />
  );
}

function AttachmentChip({ filename }: { filename: string }) {
  const icon = FILE_ICONS[fileIconForName(filename)];
  return (
    <span className="inline-flex h-11 min-w-0 max-w-[150px] shrink-0 items-center gap-1.5 rounded-md border border-border bg-notelist px-2.5 text-[13px] text-ink">
      <span className="text-accent">
        <Icon name={icon} size={16} />
      </span>
      <span className="truncate">{filename}</span>
    </span>
  );
}

export function NoteAttachmentStrip({
  attachments,
}: {
  attachments: ProjectedAttachment[];
}) {
  if (attachments.length === 0) return null;

  // Render in document order — an attachment that appears before the images in
  // the note shows first in the strip. The strip is a single, non-wrapping row
  // clipped to the note row's width: items keep their natural size and whatever
  // overflows the one line is trimmed (no "+N" tile, no item cap).
  return (
    <div className="mt-1.5 flex flex-nowrap items-center gap-1.5 overflow-hidden py-1">
      {attachments.map((att) =>
        att.kind === "image" ? (
          <AttachmentThumb key={att.src} src={att.src} alt={att.alt} />
        ) : (
          <AttachmentChip key={att.src} filename={att.alt} />
        ),
      )}
    </div>
  );
}
